import Copy from '@react-spectrum/s2/icons/Copy';
import { useCallback } from 'react';

import { ClaudeLogo, OpenAiLogo } from './ProviderLogos';
import {
	PROVIDERS,
	copyImportPrompt,
	openProviderPopup,
	type Provider,
} from './providers';

type ProviderPickListProps = {
	websiteUrl?: string | null;
	instagramHandle?: string | null;
	onPick: (provider: Provider) => void;
};

const PROVIDER_ORDER: Provider[] = ['chatgpt', 'claude'];

/**
 * The provider cards (ChatGPT / Claude) that copy the import prompt and open
 * the provider in a popup. Shared between the onboarding select screen and the
 * settings Memories card.
 */
export const ProviderPickList = ({
	websiteUrl,
	instagramHandle,
	onPick,
}: ProviderPickListProps) => {
	const handlePick = useCallback(
		(provider: Provider) => {
			// Clipboard + window.open must run synchronously inside the gesture.
			copyImportPrompt(provider, {
				websiteUrl: websiteUrl ?? '',
				instagramHandle: instagramHandle ?? '',
			});
			openProviderPopup(PROVIDERS[provider].chatUrl);
			onPick(provider);
		},
		[websiteUrl, instagramHandle, onPick],
	);

	return (
		<div className="mb-6 flex flex-col" style={{ gap: 12 }}>
			{PROVIDER_ORDER.map((id) => {
				const p = PROVIDERS[id];
				const Logo = id === 'chatgpt' ? OpenAiLogo : ClaudeLogo;
				return (
					<button
						key={id}
						type="button"
						onClick={() => handlePick(id)}
						className="flex w-full cursor-pointer items-center gap-3.5
							rounded-2xl border border-[#e8e6e0] bg-white px-4 py-3.5 text-left
							shadow-[0_1px_2px_rgba(20,16,12,0.04)] transition-all
							hover:-translate-y-px hover:border-[#d8d5cc]
							hover:shadow-[0_10px_28px_rgba(40,30,20,0.10)]
							active:translate-y-0 motion-reduce:transition-none"
					>
						<span
							aria-hidden="true"
							className="flex shrink-0 items-center justify-center"
							style={{
								width: 44,
								height: 44,
								borderRadius: 12,
								background: p.chipBg,
								color: p.logoColor,
							}}
						>
							<Logo size={24} />
						</span>
						<span
							style={{
								fontSize: 16,
								fontWeight: 600,
								color: 'var(--text-primary)',
							}}
						>
							{p.label}
						</span>
						<span
							aria-hidden="true"
							className="ml-auto inline-flex shrink-0 items-center"
							style={
								{
									gap: 6,
									fontSize: 13,
									fontWeight: 500,
									color: 'var(--text-muted)',
									// S2 icons paint with var(--iconPrimary); match the text.
									'--iconPrimary': 'var(--text-muted)',
								} as React.CSSProperties
							}
						>
							<Copy />
							Copy prompt
						</span>
					</button>
				);
			})}
		</div>
	);
};
